import { Layer } from '@app/modules/cop/store/cop/models';
import { StandardFeature } from '../standard/standard.feature';
import { Dataset, Document } from '@app/modules/core/store/models';
import { MarkerUrls } from '../feature.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { v4 as uuidv4 } from 'uuid';

export const SELECTED_Z_INDEX = 10000;
export class AlertSamsaraVehicleFeature extends StandardFeature {
  protected markerIconOptions = {
    size: new google.maps.Size(64, 68),
    anchor: new google.maps.Point(32, 63),
    scaledSize: new google.maps.Size(54.4, 57.8)
  };

  buildMarkerImageUrls(layer: Layer, dataset: Dataset, document: Document, cacheBuster: number): MarkerUrls {
    return {
      normal: `/api/v1/meta/markers/samsara-alert-pin/0/0/0/normal/samsara-alert-pin_${cacheBuster}.png`,
      selected: `/api/v1/meta/markers/samsara-alert-pin/0/0/0/selected/samsara-alert-pin_${cacheBuster}.png`
    };
  }

  addPoint(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const [lng = null, lat = null] = geometry?.coordinates ?? [];
    
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? this.markerUrls.normal : this.markerUrls.selected,
        ...this.markerIconOptions
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      geoField
    };

    const mapEventService = context.mapEvents;

    marker.addListener('mouseover', event => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: marker.getPosition(),
          offsetY: 27,
          offsetX: -4,
          layer,
          dataset,
          document,
          offsetBelow: event.domEvent.target.clientHeight / 2
        });
      }
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    this.googleMapFeatures.markers.push(marker);
    this.clusterService.registerMarkerAdd(this.layerUId, marker);

    if (layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  getMarkerAnchorPoint(layer: Layer): google.maps.Point {
    return this.markerIconOptions.anchor;
  }

  setMarkersSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.markerUrls.selected,
        ...this.markerIconOptions
      });
    }
  }

  setMarkersNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      marker.setZIndex(zIndex);

      marker.setIcon({
        url: this.markerUrls.normal,
        ...this.markerIconOptions
      });
    }
  }
}
